import { Injectable, signal, effect } from '@angular/core';

export type ThemeMode = 'light' | 'dark' | 'system';
export type EffectiveTheme = 'light' | 'dark';

/**
 * ThemeService - Centralized theme management
 * 
 * Manages Light, Dark, and System theme modes with:
 * - Reactive state using Angular signals
 * - System theme detection via prefers-color-scheme
 * - localStorage persistence
 * - Automatic OS theme change detection
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly STORAGE_KEY = 'ledger_one_theme';
  private readonly DEFAULT_THEME: ThemeMode = 'light';
  
  // Current theme mode (light/dark/system)
  private readonly _themeMode = signal<ThemeMode>(this.DEFAULT_THEME);
  
  // Effective theme (resolves system to light/dark)
  private readonly _effectiveTheme = signal<EffectiveTheme>('light');
  
  // Public readonly signals
  readonly themeMode = this._themeMode.asReadonly();
  readonly effectiveTheme = this._effectiveTheme.asReadonly();
  
  private systemThemeMediaQuery?: MediaQueryList;
  private systemThemeListener?: (e: MediaQueryListEvent) => void;

  constructor() {
    // Effect to update effective theme when mode changes
    effect(() => {
      const mode = this._themeMode();
      const effective = this.resolveEffectiveTheme(mode);
      this._effectiveTheme.set(effective);
      this.applyTheme(effective);
    });

    // Effect to listen for system theme changes when in system mode
    effect(() => {
      const mode = this._themeMode();
      if (mode === 'system') {
        this.setupSystemThemeListener();
      } else {
        this.removeSystemThemeListener();
      }
    });
  }

  /**
   * Initialize theme from localStorage or default to system
   * Should be called during app bootstrap
   */
  initializeTheme(): void {
    const savedTheme = this.getStoredTheme();
    if (savedTheme && this.isValidTheme(savedTheme)) {
      this._themeMode.set(savedTheme);
    } else {
      this._themeMode.set(this.DEFAULT_THEME);
    }
  }

  /**
   * Set theme mode
   */
  setTheme(mode: ThemeMode): void {
    if (!this.isValidTheme(mode)) {
      console.warn(`Invalid theme mode: ${mode}`);
      return;
    }

    this._themeMode.set(mode);
    this.storeTheme(mode);
  }

  /**
   * Get current theme mode
   */
  getTheme(): ThemeMode {
    return this._themeMode();
  }

  /**
   * Get effective theme (resolves system to light/dark)
   */
  getEffectiveTheme(): EffectiveTheme {
    return this._effectiveTheme();
  }

  /**
   * Resolve system theme to actual light/dark based on OS preference
   */
  private resolveEffectiveTheme(mode: ThemeMode): EffectiveTheme {
    if (mode === 'system') {
      return this.detectSystemTheme();
    }
    return mode;
  }

  /**
   * Detect system theme preference
   */
  private detectSystemTheme(): EffectiveTheme {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light'; // Default to light if detection unavailable
  }

  /**
   * Apply theme class to document body (single source of truth)
   */
  private applyTheme(theme: EffectiveTheme): void {
    if (typeof document === 'undefined') {
      return;
    }

    const body = document.body;
    if (!body) {
      return;
    }

    // Remove theme classes from body (dark-theme = CSS variable overrides; dark = Tailwind dark: variants)
    body.classList.remove('dark-theme', 'dark');

    if (theme === 'dark') {
      body.classList.add('dark-theme', 'dark');
    }

    // Update data attribute on html for potential CSS selectors
    if (document.documentElement) {
      document.documentElement.setAttribute('data-theme', theme);
    }
  }

  /**
   * Setup listener for system theme changes
   */
  private setupSystemThemeListener(): void {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    this.removeSystemThemeListener();

    this.systemThemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.systemThemeListener = (e: MediaQueryListEvent) => {
      if (this._themeMode() === 'system') {
        const newEffectiveTheme = e.matches ? 'dark' : 'light';
        this._effectiveTheme.set(newEffectiveTheme);
        this.applyTheme(newEffectiveTheme);
      }
    };

    // Modern browsers
    if (this.systemThemeMediaQuery.addEventListener) {
      this.systemThemeMediaQuery.addEventListener('change', this.systemThemeListener);
    } else {
      // Fallback for older browsers
      this.systemThemeMediaQuery.addListener(this.systemThemeListener);
    }
  }

  /**
   * Remove system theme listener
   */
  private removeSystemThemeListener(): void {
    if (this.systemThemeMediaQuery && this.systemThemeListener) {
      if (this.systemThemeMediaQuery.removeEventListener) {
        this.systemThemeMediaQuery.removeEventListener('change', this.systemThemeListener);
      } else {
        // Fallback for older browsers
        this.systemThemeMediaQuery.removeListener(this.systemThemeListener);
      }
    }
    this.systemThemeMediaQuery = undefined;
    this.systemThemeListener = undefined;
  }

  /**
   * Store theme preference in localStorage
   */
  private storeTheme(theme: ThemeMode): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, theme);
    } catch (error) {
      console.warn('Failed to store theme preference:', error);
    }
  }

  /**
   * Get stored theme preference from localStorage
   */
  private getStoredTheme(): ThemeMode | null {
    try {
      return localStorage.getItem(this.STORAGE_KEY) as ThemeMode | null;
    } catch (error) {
      console.warn('Failed to read theme preference:', error);
      return null;
    }
  }

  /**
   * Validate theme mode
   */
  private isValidTheme(theme: string): theme is ThemeMode {
    return theme === 'light' || theme === 'dark' || theme === 'system';
  }
}

